import { Session } from "@insite/client-framework/Services/SessionService";
import { ProductModel } from "@insite/client-framework/Types/ApiModels";

// OOTB from file /modules/client-framework/src/Common/Utilities/tracking.ts
// get global for google tag manager
export function getDataLayer() {
    if (!(window as any).dataLayer) {
        (window as any).dataLayer = [];
    }
    return (window as any).dataLayer;
}

function setData(eventData: any) {
    const dataLayer = getDataLayer();
    if (dataLayer) {
        dataLayer.push(eventData);
    } else {
        (window as any).dataLayer = [eventData];
    }
}

interface CustomProductList {
    item_name: string;
    item_id: string;
    price: number;
    item_brand: string;
    item_category: string;
    item_category2: string;
    item_category3: string;
    item_category4: string;
    quantity: string;
}

function setItems(
    cartLines: any[],
    productDetails: any[],
): { customProductList: CustomProductList[]; totalValue: number } {
    const customProductList: CustomProductList[] = [];
    let totalValue = 0;

    cartLines.forEach(
        (cartlineitem: {
            productId: string;
            erpNumber: string;
            shortDescription: string;
            pricing: { actualPrice: number; extendedActualPrice?: number };
            qtyOrdered: string;
            brand: { name: string };
        }) => {
            const customCategoryData = productDetails.find(item => item.id === cartlineitem.productId) ?? {};
            customProductList.push({
                item_name: cartlineitem.shortDescription ?? "",
                item_id: cartlineitem.erpNumber ?? "",
                price: cartlineitem.pricing.actualPrice ?? 0,
                item_brand: cartlineitem.brand?.name ?? customCategoryData.brandName ?? "",
                item_category: customCategoryData.categories.item_category ?? "",
                item_category2: customCategoryData.categories.item_category2 ?? "",
                item_category3: customCategoryData.categories.item_category3 ?? "",
                item_category4: customCategoryData.categories.item_category4 ?? "",
                quantity: cartlineitem.qtyOrdered ?? "",
            });

            if (cartlineitem.pricing.extendedActualPrice) {
                totalValue += cartlineitem.pricing.extendedActualPrice;
            } else {
                totalValue += cartlineitem.pricing.actualPrice * parseInt(cartlineitem.qtyOrdered, 10);
            }
        },
    );

    return { customProductList, totalValue };
}

export function trackUserInfoEvent(
    session?: Session & { billToNumber?: string; shipToNumber?: string; location: string },
) {
    if (IS_SERVER_SIDE) {
        return;
    }

    const userInfo = session;
    const eventData = {
        event: "user_info",
        is_logged_in: `${(session?.isAuthenticated && !session?.isGuest) ?? false}`,
        user_id: "",
        bill_to_id: "",
        ship_to_id: "",
        location: session?.location ?? "/",
    };
    if (userInfo?.isAuthenticated) {
        const { userName, billToNumber, shipToNumber } = userInfo;
        eventData["user_id"] = userName;
        eventData["bill_to_id"] = billToNumber ?? "";
        eventData["ship_to_id"] = shipToNumber ?? "";
    }

    setData(eventData);
}

export function trackViewItemEvent(websiteName: string, product: ProductModel, categories: string[]) {
    if (IS_SERVER_SIDE) {
        return;
    }

    const itemInfo: any = {
        item_id: product.productNumber.trim(),
        item_name: product.productTitle.trim(),
        affiliation: websiteName.trim(),
        item_brand: product.brand?.name.trim() ?? "",
        price: product.unitListPrice ?? "",
        quantity: "1",
    };
    categories.forEach((category, idx) => {
        itemInfo[`item_category${idx > 0 ? idx + 1 : ""}`] = category.trim();
    });

    // select item event
    const selectitemEventData = {
        event: "select_item",
        currency: "USD",
        value: product.unitListPrice ?? "",
        ecommerce: {
            items: [itemInfo],
        },
    };

    const eventData = {
        event: "view_item",
        currency: "USD",
        value: product.unitListPrice ?? "",
        ecommerce: {
            items: [itemInfo],
        },
    };

    setData({ ecommerce: null });
    setData(selectitemEventData);
    setData(eventData);
}

export function trackAddToCartEvent(cartLines: any[], productDetails: any[]) {
    if (IS_SERVER_SIDE) {
        return;
    }

    const { customProductList, totalValue } = setItems(cartLines, productDetails);

    const eventData = {
        event: "add_to_cart",
        ecommerce: {
            currency: "USD",
            value: totalValue,
            items: customProductList,
        },
    };

    setData({ ecommerce: null });
    setData(eventData);
}

export function trackRemoveFromCartEvent(cartLines: any[], productDetails: any[]) {
    if (IS_SERVER_SIDE) {
        return;
    }

    const { customProductList, totalValue } = setItems(cartLines, productDetails);

    const eventData = {
        event: "remove_from_cart",
        ecommerce: {
            currency: "USD",
            value: totalValue,
            items: customProductList,
        },
    };

    setData({ ecommerce: null });
    setData(eventData);
}

export function trackViewCartEvent(cart: any, productDetails: any[]) {
    if (IS_SERVER_SIDE) {
        return;
    }

    const { customProductList } = setItems(cart.cartLines, productDetails);

    const eventData = {
        event: "view_cart",
        ecommerce: {
            currency: "USD",
            value: cart.orderSubTotal ?? 0,
            items: customProductList,
        },
    };

    setData(eventData);
}

export function trackBeginCheckoutEvent(cart: any, productDetails: any[]) {
    if (IS_SERVER_SIDE) {
        return;
    }

    const { customProductList } = setItems(cart.cartLines, productDetails);

    const eventData = {
        event: "begin_checkout",
        ecommerce: {
            currency: "USD",
            value: cart.orderGrandTotal ?? 0,
            items: customProductList,
        },
    };

    setData(eventData);
}

export function trackAddShippingInfoEvent(cart: any, productDetails: any[]) {
    if (IS_SERVER_SIDE) {
        return;
    }

    const { customProductList } = setItems(cart.cartLines, productDetails);

    const eventData = {
        event: "add_shipping_info",
        ecommerce: {
            currency: "USD",
            value: cart.orderGrandTotal ?? 0,
            shipping_tier: cart.carrier.description ?? "",
            items: customProductList,
        },
    };

    setData(eventData);
}

export function trackAddPaymentInfoEvent(cart: any, productDetails: any[]) {
    if (IS_SERVER_SIDE) {
        return;
    }

    const { customProductList } = setItems(cart.cartLines, productDetails);

    const eventData = {
        event: "add_payment_info",
        ecommerce: {
            currency: "USD",
            value: cart.orderGrandTotal ?? 0,
            payment_type: cart.paymentMethod.description ?? "",
            items: customProductList,
        },
    };

    setData(eventData);
}

export function trackPurchaseEvent(cart: any, productDetails: any[]) {
    if (IS_SERVER_SIDE) {
        return;
    }

    const { customProductList } = setItems(cart.cartLines, productDetails);

    const eventData = {
        event: "purchase",
        ecommerce: {
            currency: "USD",
            transaction_id: cart.orderNumber ?? "",
            tax: cart.totalTax ?? 0,
            shipping: cart.shippingAndHandling ?? 0,
            value: cart.orderGrandTotal ?? 0,
            items: customProductList,
        },
    };

    setData(eventData);
}

export function trackViewItemListEvent(
    cartLines: any[],
    listName: string,
    productDetails: any[],
    orderGrandTotal: number,
) {
    if (IS_SERVER_SIDE) {
        return;
    }

    const { customProductList } = setItems(cartLines, productDetails);

    const eventData = {
        event: "view_item_list",
        // put list name on lowercase em swap spaces for underscores,
        item_list_id: listName.toLowerCase().replace(/ /g, "_"),
        item_list_name: listName,
        ecommerce: {
            currency: "USD",
            value: orderGrandTotal,
            items: customProductList,
        },
    };

    setData(eventData);
}

export function trackQuickOrderEvent() {
    if (IS_SERVER_SIDE) {
        return;
    }

    const eventData = {
        event: "cp_quick_order",
    };

    setData(eventData);
}
export function trackTaxCertificateUploadEvent() {
    if (IS_SERVER_SIDE) {
        return;
    }

    const eventData = {
        event: "tax_file_upload",
    };

    setData(eventData);
}
